import {
  User,
  Building2,
  Calculator,
  FileText,
  Key,
  MessageSquare,
  Activity,
  Users,
  Target,
  TreePalm,
  CircleDollarSign,
  IdCardLanyard,
  UserLock,
  Calendar,
} from "lucide-react";
import * as constantValues from "../constants/constants";

const hasNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

export const shouldRender = (key, value) =>
  !constantValues.optionalFields.has(key) || hasNonEmptyString(value);

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d) ? null : d;
  }
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const d = new Date(`${value}T00:00:00`);
      return isNaN(d) ? null : d;
    }
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : new Date(t);
  }
  return null;
};

const parseYmdDate = (ymd) => {
  if (!ymd) return null;
  const parts = ymd.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part)))
    return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
};

export const formatDate = (ymd) => {
  const date = toDate(ymd);
  return date ? constantValues.DATE_WITH_TIME_FORMATTER.format(date) : "—";
};

export const formatDataDate = (ymd) => {
  const date = parseYmdDate(ymd);
  return date ? constantValues.DATE_ONLY_FORMATTER.format(date) : "—";
};

export const formatTime = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  return isNaN(date)
    ? "—"
    : constantValues.ISO_DATE_TIME_FORMATTER.format(date);
};

export const formatDuration = (ms) => {
  const milliseconds = Number(ms) || 0;
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => String(value).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const formatSSN = (value) => {
  if (!value) return value || "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  return value;
};

export const formatEIN = (value) => {
  if (!value) return value || "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 9) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  return value;
};

export const formatPercentDisplay = (value, { maxDecimals = 2 } = {}) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(String(value).trim().replace(/%/g, ""));
  if (Number.isNaN(numeric)) return String(value);
  const formatted = Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(maxDecimals);
  return `${formatted}%`;
};

export const formatBytes = (bytes) => {
  if (!Number(bytes)) return "";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const generateCredentialId = () =>
  `cred_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const normalizeNotes = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe && typeof maybe === "object") return Object.values(maybe);
  return [];
};

const normalizeCredentials = (maybe) => {
  if (Array.isArray(maybe)) {
    return maybe.map((entry) => ({
      id: entry?.id || entry?._id || generateCredentialId(),
      entity_name: entry?.entity_name || "",
      entity_link: entry?.entity_link || "",
      username: entry?.username || "",
      password: entry?.password || "",
    }));
  }

  if (maybe && typeof maybe === "object") {
    return [
      {
        id: maybe?.id || maybe?._id || generateCredentialId(),
        entity_name: maybe?.entity_name || "",
        entity_link: maybe?.entity_link || "",
        username: maybe?.username || "",
        password: maybe?.password || "",
      },
    ];
  }

  return [];
};

export const prepareClientData = (data) => {
  if (!data) return data;
  return {
    ...data,
    credentials: normalizeCredentials(data.credentials),
    notes: normalizeNotes(data.notes),
  };
};

export const getChipClasses = (type) =>
  `px-2 py-1 text-xs font-medium rounded-full ${
    constantValues.CHIP_STYLE_MAP[type] || constantValues.CHIP_STYLE_MAP.default
  }`;

export const createTabs = ({
  userPermission,
  clientType,
  dependantsCount,
  notesCount,
  sessionsCount,
  credentialsCount,
  ownershipCount,
}) => {
  const isPerson = clientType === "person";
  const isRestricted = userPermission === "restrictedAccess";

  const tabs = [
    {
      id: "data",
      label: "Information",
      icon: isPerson ? User : Building2,
    },
    ...(isPerson
      ? [
          {
            id: "dependants",
            label: "Dependants",
            icon: Users,
            count: dependantsCount,
          },
        ]
      : []),
    ...(!isPerson
      ? [{ id: "quickbooks", label: "QuickBooks", icon: Calculator }]
      : []),
    { id: "documents", label: "Documents", icon: FileText },
    ...(!isRestricted
      ? [
          {
            id: "credentials",
            label: "Credentials",
            icon: Key,
            count: credentialsCount,
          },
        ]
      : []),
    { id: "notes", label: "Notes", icon: MessageSquare, count: notesCount },
    { id: "tracking", label: "Tracking", icon: Activity, count: sessionsCount },
    ...(isPerson
      ? [{ id: "quickbooks", label: "QuickBooks", icon: Calculator }]
      : []),
    ...(!isPerson && !isRestricted
      ? [
          {
            id: "ownership",
            label: "Ownership",
            icon: Users,
            count: ownershipCount,
          },
        ]
      : []),
  ];

  return tabs;
};

export const getInitials = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export const formatJoinedDate = (dateText) => {
  if (!dateText) return "—";
  const [y, m, d] = dateText.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

export const createLeadTabs = ({ notesCount, credentialsCount = 0 }) => {
  const tabs = [
    { id: "data", label: "Information", icon: Target },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "quickbooks", label: "QuickBooks", icon: Calculator },
    {
      id: "credentials",
      label: "Credentials",
      icon: Key,
      count: credentialsCount,
    },
    { id: "notes", label: "Notes", icon: MessageSquare, count: notesCount },
  ];

  return tabs;
};

export const formatValue = (value) => {
  const EMPTY_VALUE = "N/A";

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : EMPTY_VALUE;
  }
  if (value === null || value === undefined) return EMPTY_VALUE;
  const text = String(value).trim();
  return text ? text : EMPTY_VALUE;
};

export const createEmployeeTabs = ({ requestsCount }) => {
  const tabs = [
    {
      id: "data",
      label: "Information",
      icon: IdCardLanyard,
    },
    { id: "personal", label: "Personal", icon: UserLock },
    { id: "accounting", label: "Accounting", icon: CircleDollarSign },
    {
      id: "vacations",
      label: "Vacations",
      icon: TreePalm,
      count: requestsCount,
    },
    {
      id: "holidays",
      label: "Holidays",
      icon: Calendar,
    },
  ];

  return tabs;
};
